import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt, faUndo, faSearch, faPrint, faFileExport, faEnvelope, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { useBreadcrumb } from "../../contexts/BreadcrumbContext";
import "./LookupPatientRPM.css";
import "../BillingReports/ManageBillingReport.css";
import patientService from "../../services/patientService";
import practiceService from "../../services/practiceService";
import Heading from "../../components/Common/Heading/Heading";
import ListScreenSearchBar from "../../components/Common/ListScreenSearchBar/ListScreenSearchBar";
import ListScreenPaginationBar from "../../components/Common/ListScreenPaginationBar/ListScreenPaginationBar";
import StatusBadge from "../../components/Common/StatusBadge/StatusBadge";
import Table from "../../components/Common/Table/Table";
import "../../components/Common/ListScreenPageLayout/listScreenPage.css";
import EmailModal from "../../components/EmailModal/EmailModal";
import emailService from "../../services/emailService";
import { exportElementToPdf } from "../../utils/reportExportUtils";
import {
  buildPatientDetailsPath,
  resolvePatientPracticeId,
  resolvePatientRecordId
} from "../../utils/patientRouteUtils";

/** Parse API RPM values (ISO, MM/DD/YYYY, etc.) to epoch ms for sorting. */
function parseFlexibleDateToMs(value) {
  if (value == null || value === "") return 0;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const s = String(value).trim();
  if (!s) return 0;
  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  if (us) {
    const m = Number(us[1]);
    const d = Number(us[2]);
    const y = Number(us[3]);
    const t = new Date(y, m - 1, d).getTime();
    if (!Number.isNaN(t)) return t;
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const t = new Date(s).getTime();
    if (!Number.isNaN(t)) return t;
  }
  const p = Date.parse(s);
  return Number.isFinite(p) ? p : 0;
}

function formatDateForInput(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().split("T")[0];
}

function transformPatientDataForRPM(patients) {
  return patients.map((patient) => {
    const patientName =
      patient.patientName ||
      patient.patient_name ||
      (patient.first_name && patient.last_name ? `${patient.first_name} ${patient.last_name}` : "") ||
      patient.name ||
      "";

    const dob =
      patient.dob ||
      patient.date_of_birth ||
      patient.dateOfBirth ||
      (patient.dob ? new Date(patient.dob).toLocaleDateString() : "") ||
      "";

    const model =
      patient.model || patient.model_number || patient.modelNumber || patient.modelString || "";

    const serial =
      patient.serial || patient.serial_number || patient.serialNumber || patient.serialString || "";

    const rawRpmForSort =
      patient.rpm_start_date ?? patient.rpmStartDate ?? patient.rpm_startDate ?? null;
    const rpmStartDateSort = rawRpmForSort ? parseFlexibleDateToMs(rawRpmForSort) : 0;

    const rpmStartDate =
      patient.rpmStartDate ||
      patient.rpm_start_date ||
      patient.rpm_startDate ||
      (patient.rpm_start_date ? new Date(patient.rpm_start_date).toLocaleDateString() : "") ||
      "";

    const uploadDate =
      patient.uploadDate ||
      patient.upload_date ||
      (patient.upload_date ? new Date(patient.upload_date).toLocaleString() : "") ||
      "";

    const measurementDays =
      patient.measurementDays !== undefined
        ? patient.measurementDays
        : patient.measurement_days !== undefined
          ? patient.measurement_days
          : "-";

    const serviceTime = patient.serviceTime || patient.service_time || "";

    const eligibleCptCodes = patient.eligibleCptCodes || patient.eligible_cpt_codes || "";

    let status = patient.status || patient.status_id || "";
    if (status && status !== "Unknown") {
      if (typeof status === "string" && !/^\d+$/.test(status)) {
        /* keep label */
      } else if (typeof status === "number" || (typeof status === "string" && /^\d+$/.test(status))) {
        const statusMap = { "2": "Active", "3": "Pending", "4": "Locked", "1": "Inactive" };
        status = statusMap[status.toString()] || status;
      }
    } else {
      status = "Unknown";
    }

    let vitals = patient.vitals || patient.vitals_id || "";
    if (typeof vitals === "number" || (typeof vitals === "string" && /^\d+$/.test(vitals))) {
      const vitalsMap = { "1": "BP", "2": "BG", "3": "WT" };
      vitals = vitalsMap[String(vitals)] || vitals;
    }

    return {
      ...patient,
      patient_table_id: patient.patient_table_id ?? patient.id,
      practice_id: patient.practice_id ?? patient.practiceId,
      patientName,
      dob,
      model,
      serial,
      rpmStartDate,
      rpmStartDateSort,
      uploadDate,
      measurementDays,
      serviceTime,
      eligibleCptCodes,
      status,
      vitals,
    };
  });
}

const LookupPatientRPM = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const roleId = Number(user?.role_id);
  const isScopedRole = [4, 5, 7].includes(roleId); // Provider, Caregiver, System Caregiver
  const scopedPracticeId = useMemo(() => {
    const pid = user?.practice_id ?? user?.practiceId ?? user?.practices?.[0]?.id;
    return pid != null && pid !== "" ? String(pid) : "";
  }, [user?.practiceId, user?.practice_id, user?.practices]);
  const { updateBreadcrumbs } = useBreadcrumb();

  useEffect(() => {
    const baseCrumb =
      roleId === 4
        ? { label: 'Patient Management', path: '/patient-management' }
        : roleId === 5
        ? { label: 'Caregiver Dashboard', path: '/caregiver-dashboard' }
        : roleId === 7
          ? { label: 'System Caregiver Dashboard', path: '/system-caregiver-dashboard' }
          : { label: 'Patient Management', path: '/patient-management' };
    updateBreadcrumbs([
      baseCrumb,
      { label: 'Look Up Patient (By RPM Date)', path: null, active: true }
    ]);
  }, [roleId, updateBreadcrumbs]);

  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [patients, setPatients] = useState([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [querySubmitting, setQuerySubmitting] = useState(false);
  const [entriesPerPage, setEntriesPerPage] = useState("10");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPatients, setTotalPatients] = useState(0);
  const [dateRangeDisplay, setDateRangeDisplay] = useState("");
  const [practiceId, setPracticeId] = useState("");
  const [activeRpmQuery, setActiveRpmQuery] = useState({ practiceId: "", start: "", end: "" });
  const [practices, setPractices] = useState([]);
  const hasInitializedRef = useRef(false);
  const hasInitialTableLoadRef = useRef(false);
  const isInitialTableLoadRef = useRef(false);
  const latestRpmRequestRef = useRef(0);
  const [hasQueryAttempted, setHasQueryAttempted] = useState(false);
  const [dateFieldErrors, setDateFieldErrors] = useState({ start: "", end: "" });
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: "rpmStartDate", direction: "desc" });
  const isAdminOrSuper = user?.role_id === 1 || user?.role_id === 2;
  const cacheScopeKey = useMemo(
    () => `${roleId}:${isScopedRole ? scopedPracticeId || "scoped" : "global"}`,
    [roleId, isScopedRole, scopedPracticeId]
  );

  const hasActiveFilters = useMemo(() => {
    const hasDateFilter = Boolean(dateRange.start || dateRange.end);
    const hasPracticeFilter = isAdminOrSuper && Boolean(practiceId);
    return hasDateFilter || hasPracticeFilter;
  }, [dateRange.start, dateRange.end, isAdminOrSuper, practiceId]);

  const getEffectivePracticeTarget = () => {
    if (isScopedRole) {
      if (scopedPracticeId) return scopedPracticeId;
      const pid = user?.practice_id ?? user?.practiceId ?? user?.practices?.[0]?.id;
      return pid != null && pid !== "" ? String(pid) : "";
    }
    if (practiceId) return practiceId;
    return "all";
  };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const rpmStartInputRef = useRef(null);
  const rpmEndInputRef = useRef(null);

  const openDatePicker = (ref) => {
    const el = ref?.current;
    if (!el) return;
    try {
      el.showPicker?.();
    } catch {
      el.focus();
    }
  };

  const getStatusOrder = (status) => {
    if (!status) return 99;
    const statusStr = status.toString().toUpperCase();
    if (statusStr === "2" || statusStr === "ACTIVE" || statusStr === "A") return 1;
    if (statusStr === "3" || statusStr === "PENDING" || statusStr === "P") return 2;
    if (statusStr === "4" || statusStr === "LOCKED" || statusStr === "L") return 3;
    return 99;
  };

  const comparePrimarySort = (a, b) => {
    const key = sortConfig.key;
    if (!key) return 0;

    if (key === "rpmStartDate") {
      const av = a.rpmStartDateSort ?? 0;
      const bv = b.rpmStartDateSort ?? 0;
      if (av < bv) return -1;
      if (av > bv) return 1;
      return 0;
    }

    if (["dob", "uploadDate"].includes(key)) {
      const dateA = a[key] ? new Date(a[key]) : new Date(0);
      const dateB = b[key] ? new Date(b[key]) : new Date(0);
      const av = isNaN(dateA.getTime()) ? 0 : dateA.getTime();
      const bv = isNaN(dateB.getTime()) ? 0 : dateB.getTime();
      if (av < bv) return -1;
      if (av > bv) return 1;
      return 0;
    }

    let aValue = a[key];
    let bValue = b[key];
    if (typeof aValue === "string" && typeof bValue === "string") {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return -1;
    if (bValue == null) return 1;
    if (aValue < bValue) return -1;
    if (aValue > bValue) return 1;
    return 0;
  };

  /** Default: status priority first. Exception: when sorting by RPM Start Date, sort purely by RPM date. */
  const sortPatients = (patientsList) => {
    const dir = sortConfig.direction === "asc" ? 1 : -1;
    return [...patientsList].sort((a, b) => {
      // If user explicitly sorts by RPM Start Date, ignore other priority rules.
      if (sortConfig.key === "rpmStartDate") {
        const primary = comparePrimarySort(a, b);
        if (primary !== 0) return dir * primary;
        return String(a.patientName || "").localeCompare(String(b.patientName || ""), undefined, {
          sensitivity: "base",
        });
      }

      // 1. Primary Sort: Status Priority
      const sa = getStatusOrder(a.status);
      const sb = getStatusOrder(b.status);
      if (sa !== sb) return sa - sb;

      // 2. Secondary Sort: Based on selected column
      const primary = comparePrimarySort(a, b);
      if (primary !== 0) return dir * primary;

      // 3. Tertiary Sort: Name
      return String(a.patientName || "").localeCompare(String(b.patientName || ""), undefined, {
        sensitivity: "base",
      });
    });
  };

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportMenu && !event.target.closest('.lookup-patient-rpm-export-container')) {
        setShowExportMenu(false);
      }
    };

    if (showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showExportMenu]);

  // Load practice ID on mount
  useEffect(() => {
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;
    if (isAdminOrSuper) {
      loadPractices();
    } else if (isScopedRole && scopedPracticeId) {
      setPracticeId(scopedPracticeId);
    }
  }, [isAdminOrSuper, isScopedRole, scopedPracticeId, user]);

  const resolvePageLimit = React.useCallback((entriesValue) => {
    if (entriesValue === "all" || entriesValue === "All") {
      return 1000;
    }
    const n = Number.parseInt(String(entriesValue), 10);
    return Number.isFinite(n) && n > 0 ? n : 10;
  }, []);

  const loadRpmPatients = React.useCallback(
    async (query, page = 1, limit = 10, options = {}) => {
      const { silent = false, showToast = false, updateSubtitle = true } = options;
      const targetPracticeId = query?.practiceId;
      if (!targetPracticeId) return;
      const requestId = ++latestRpmRequestRef.current;

      if (!silent) setTableLoading(true);
      try {
        const filters = {};
        if (query?.start && query?.end) {
          filters.start = query.start;
          filters.end = query.end;
          filters.billingStartRpm = query.start;
          filters.billingEndRpm = query.end;
        }
        filters.page = page;
        filters.limit = limit;
        const response = await patientService.lookupPatientRPM(targetPracticeId, filters);

        let patientsData = [];
        if (response && response.data) {
          if (response.data.success) {
            if (response.data.data) {
              if (Array.isArray(response.data.data)) {
                patientsData = response.data.data;
              } else if (response.data.data.patients && Array.isArray(response.data.data.patients)) {
                patientsData = response.data.data.patients;
              } else if (response.data.data.data && Array.isArray(response.data.data.data)) {
                patientsData = response.data.data.data;
              }
            }
          } else if (Array.isArray(response.data)) {
            patientsData = response.data;
          } else if (response.data.patients && Array.isArray(response.data.patients)) {
            patientsData = response.data.patients;
          } else if (response.data.data && Array.isArray(response.data.data)) {
            patientsData = response.data.data;
          }
        }

        const transformedPatients = transformPatientDataForRPM(Array.isArray(patientsData) ? patientsData : []);
        if (requestId !== latestRpmRequestRef.current) return;
        setPatients(transformedPatients);
        const apiTotal = Number(response?.data?.pagination?.total ?? response?.data?.data?.pagination?.total);
        const resolvedTotal = Number.isFinite(apiTotal) ? apiTotal : transformedPatients.length;
        setTotalPatients(resolvedTotal);

        if (updateSubtitle) {
          if (query?.start && query?.end) {
            const from = new Date(query.start);
            const to = new Date(query.end);
            const formatDate = (date) => {
              const month = String(date.getMonth() + 1).padStart(2, "0");
              const day = String(date.getDate()).padStart(2, "0");
              const year = date.getFullYear();
              return `${month}-${day}-${year}`;
            };
            setDateRangeDisplay(`${formatDate(from)} ~ ${formatDate(to)}`);
          } else {
            setDateRangeDisplay("");
          }
        }

      } catch (error) {
        if (requestId !== latestRpmRequestRef.current) return;
        if (showToast) {
          toast.error("Request failed. Please try again.", { toastId: "lookup-rpm-query-error" });
        }
        setPatients([]);
        setTotalPatients(0);
        if (process.env.NODE_ENV === "development") {
          console.error("RPM Query error:", error);
        }
      } finally {
        if (!silent) setTableLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (!isScopedRole) return;
    if (scopedPracticeId) return;
    if (!user) return;
    setTableLoading(false);
  }, [isScopedRole, scopedPracticeId, user]);

  // Initial table population: skeleton until first page is fetched.
  useEffect(() => {
    const target = getEffectivePracticeTarget();
    if (!target) return;
    if (hasInitialTableLoadRef.current) return;
    if (hasQueryAttempted) return;
    if ((dateRange.start && !dateRange.end) || (!dateRange.start && dateRange.end)) return;
    if (dateRange.start && dateRange.end && dateRange.end < dateRange.start) return;

    hasInitialTableLoadRef.current = true;
    isInitialTableLoadRef.current = true;
    setTableLoading(true);
    setCurrentPage(1);
    const initialQuery = {
      practiceId: target,
      start: dateRange.start || "",
      end: dateRange.end || ""
    };
    setActiveRpmQuery(initialQuery);

    (async () => {
      try {
        await loadRpmPatients(initialQuery, 1, 10, {
          silent: true,
          showToast: false,
          updateSubtitle: true
        });
        setHasQueryAttempted(true);
      } finally {
        setTableLoading(false);
        isInitialTableLoadRef.current = false;
      }
    })();
  }, [hasQueryAttempted, loadRpmPatients, practiceId, dateRange.start, dateRange.end, user?.practice_id, user?.practices, isAdminOrSuper]);

  const loadPractices = async () => {
    try {
      const response = await practiceService.getPractices({ limit: 500, page: 1, minimal: 1 });

      // Handle different response structures
      let practicesData = [];
      if (response && response.data) {
        if (response.data.success) {
          if (response.data.data) {
            if (Array.isArray(response.data.data)) {
              practicesData = response.data.data;
            } else if (response.data.data.practices && Array.isArray(response.data.data.practices)) {
              practicesData = response.data.data.practices;
            } else if (response.data.data.data && Array.isArray(response.data.data.data)) {
              practicesData = response.data.data.data;
            }
          }
        } else if (Array.isArray(response.data)) {
          practicesData = response.data;
        } else if (response.data.practices && Array.isArray(response.data.practices)) {
          practicesData = response.data.practices;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          practicesData = response.data.data;
        }
      }

      setPractices(Array.isArray(practicesData) ? practicesData : []);
    } catch (error) {
      console.error("Error loading practices:", error);
    }
  };

  const handleReset = () => {
    setDateRange({ start: "", end: "" });
    setSearch("");
    setDateRangeDisplay("");
    setPracticeId("");
    setCurrentPage(1);
    setHasQueryAttempted(false);
    setDateFieldErrors({ start: "", end: "" });
    setPatients([]);
    setTotalPatients(0);
    setActiveRpmQuery({ practiceId: "", start: "", end: "" });
    hasInitialTableLoadRef.current = false;
  };

  const handleQuery = async (e) => {
    e.preventDefault();
    const targetPracticeId = getEffectivePracticeTarget();

    setDateFieldErrors({ start: "", end: "" });

    if ((dateRange.start && !dateRange.end) || (!dateRange.start && dateRange.end)) {
      if (dateRange.start && !dateRange.end) {
        setDateFieldErrors({
          start: "",
          end: "Please Select End Date",
        });
      } else {
        setDateFieldErrors({
          start: "Select a start date, or clear the end date.",
          end: "",
        });
      }
      return;
    }
    if (dateRange.start && dateRange.end && dateRange.end < dateRange.start) {
      setDateFieldErrors({
        start: "",
        end: "End date cannot be before start date.",
      });
      return;
    }

    setHasQueryAttempted(true);
    setCurrentPage(1);
    const committedQuery = {
      practiceId: targetPracticeId,
      start: dateRange.start || "",
      end: dateRange.end || ""
    };
    setActiveRpmQuery(committedQuery);

    setQuerySubmitting(true);
    setTableLoading(true);
    try {
      const effectiveLimit = resolvePageLimit(entriesPerPage);
      await loadRpmPatients(committedQuery, 1, effectiveLimit, {
        silent: true,
        showToast: true,
        updateSubtitle: true
      });
    } finally {
      setTableLoading(false);
      setQuerySubmitting(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExport = async (format = 'csv') => {
    const exportRows = sortPatients(filteredPatients);
    if (exportRows.length === 0) {
      toast.warning("No data to export");
      return;
    }

    if (isExporting) return;
    setIsExporting(true);
    setShowExportMenu(false);

    try {
      if (format === 'csv') {
        const headers = ['#', 'Status', 'Vitals', 'Patient Name', 'DOB', 'Modal #', 'Serial #',
          'RPM Start Date', 'Upload Date/Time', 'Measurement Days', 'Service Time', 'Eligible CPT Codes'];

        const rows = exportRows.map((p, idx) => [
          idx + 1,
          p.status || '',
          p.vitals || '',
          p.patientName || '',
          p.dob || '',
          p.model || '',
          p.serial || '',
          p.rpmStartDate || '',
          p.uploadDate || '',
          p.measurementDays || '',
          p.serviceTime || '',
          p.eligibleCptCodes?.replace(/<br>/g, ', ') || ''
        ]);

        const csvContent = [
          headers.join(','),
          ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `patient-lookup-rpm-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("CSV exported successfully");
      } else {
        const escapeHtml = (value) =>
          String(value ?? "")
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");

        const headers = ['#', 'Status', 'Vitals', 'Patient Name', 'DOB', 'Modal #', 'Serial #',
          'RPM Start Date', 'Upload Date/Time', 'Measurement Days', 'Service Time', 'Eligible CPT Codes'];

        const tableRowsHtml = exportRows.map((p, idx) => `
          <tr>
            <td>${idx + 1}</td>
            <td>${escapeHtml(p.status || '')}</td>
            <td>${escapeHtml(p.vitals || '')}</td>
            <td>${escapeHtml(p.patientName || '')}</td>
            <td>${escapeHtml(formatTableDate(p.dob))}</td>
            <td>${escapeHtml(p.model || '')}</td>
            <td>${escapeHtml(p.serial || '')}</td>
            <td>${escapeHtml(formatTableDate(p.rpmStartDate))}</td>
            <td>${escapeHtml(p.uploadDate || '')}</td>
            <td>${escapeHtml(p.measurementDays ?? '')}</td>
            <td>${escapeHtml(p.serviceTime || '')}</td>
            <td>${escapeHtml((p.eligibleCptCodes || '').replace(/<br\s*\/?>/gi, ', '))}</td>
          </tr>
        `).join('');

        const container = document.createElement('div');
        container.style.position = 'fixed';
        container.style.left = '-99999px';
        container.style.top = '0';
        container.style.width = '1200px';
        container.innerHTML = `
          <div style="font-family: Arial, sans-serif; color: #111; padding: 16px;">
            <h2 style="margin: 0 0 10px 0;">Look Up Patient (By RPM Date)</h2>
            <div style="margin: 0 0 12px 0; font-size: 12px;">
              Generated: ${escapeHtml(new Date().toLocaleString())}
            </div>
            <table style="width: 100%; border-collapse: collapse; font-size: 10px;">
              <thead>
                <tr>${headers.map((h) => `<th style="border: 1px solid #999; padding: 4px; background: #f2f4f7; text-align: left;">${escapeHtml(h)}</th>`).join('')}</tr>
              </thead>
              <tbody>${tableRowsHtml}</tbody>
            </table>
          </div>
        `;
        document.body.appendChild(container);

        try {
          await exportElementToPdf(
            container,
            `patient-lookup-rpm-${new Date().toISOString().split('T')[0]}.pdf`,
            { jsPDF: { unit: 'in', format: 'letter', orientation: 'landscape' } }
          );
          toast.success("PDF exported successfully");
        } finally {
          document.body.removeChild(container);
        }
      }
    } catch {
      toast.error(`Failed to export ${format.toUpperCase()}`);
    } finally {
      setIsExporting(false);
    }
  };

  // Filter patients by search
  const filteredPatients = patients.filter((p) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      p.patientName?.toLowerCase().includes(searchLower) ||
      p.model?.toLowerCase().includes(searchLower) ||
      p.serial?.toLowerCase().includes(searchLower) ||
      p.rpmStartDate?.toLowerCase().includes(searchLower)
    );
  });

  const sortedAndFilteredPatients = sortPatients(filteredPatients);

  const pageSize = useMemo(() => resolvePageLimit(entriesPerPage), [entriesPerPage, resolvePageLimit]);

  const totalPages = Math.max(1, Math.ceil(totalPatients / pageSize) || 1);
  const safeCurrentPage = Math.min(Math.max(1, currentPage), totalPages);
  const startIndex = (safeCurrentPage - 1) * pageSize;

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const defaultEmailSubject = useMemo(() => {
    const range =
      dateRange.start && dateRange.end
        ? `${dateRange.start} – ${dateRange.end}`
        : "All dates";
    return `Look Up Patient (By RPM Date) — ${range}`;
  }, [dateRange.start, dateRange.end]);

  const defaultEmailContent = useMemo(() => {
    const lines = sortedAndFilteredPatients.map(
      (p, i) =>
        `${i + 1}. ${p.patientName || "-"} | RPM: ${p.rpmStartDate || "-"} | Status: ${p.status || "-"}`
    );
    return `Total rows: ${sortedAndFilteredPatients.length}\n\n${lines.join("\n")}`;
  }, [sortedAndFilteredPatients]);

  const handleEmail = () => {
    if (sortedAndFilteredPatients.length === 0) {
      toast.warning("No data to email");
      return;
    }
    setShowEmailModal(true);
  };

  const handlePageChange = (newPage) => {
    const validatedPage = Math.max(1, Math.min(newPage, totalPages));
    if (validatedPage !== currentPage) {
      setCurrentPage(validatedPage);
      if (hasQueryAttempted && activeRpmQuery.practiceId) {
        loadRpmPatients(
          activeRpmQuery,
          validatedPage,
          resolvePageLimit(entriesPerPage),
          { silent: false, showToast: false, updateSubtitle: false }
        );
      }
    }
  };

  const formatTableDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const ms = parseFlexibleDateToMs(dateString);
      const date = ms > 0 ? new Date(ms) : new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    } catch {
      return dateString;
    }
  };

  const tableRowNumber = (idx) => {
    return startIndex + idx + 1;
  };

  const handlePatientRowClick = (patient) => {
    const patientId = resolvePatientRecordId(patient);
    const practiceIdForRoute = resolvePatientPracticeId(
      patient,
      practiceId || (isScopedRole ? scopedPracticeId : "")
    );
    const path = buildPatientDetailsPath(practiceIdForRoute, patientId);
    if (!path) {
      toast.error("Unable to open patient details. Select a practice first.");
      return;
    }
    navigate(path);
  };

  const columns = [
    {
      key: "index",
      title: "#",
      sortable: false,
      className: "reusable-table-centered-col",
      render: (_p, idx) => tableRowNumber(idx),
    },
    { key: "status", title: "Status", sortable: true, className: "reusable-table-centered-col", render: (p) => <StatusBadge status={p.status} /> },
    { key: "vitals", title: "Vitals", sortable: true, render: (p) => p.vitals || "-" },
    { key: "patientName", title: "Patient Name", sortable: true, render: (p) => p.patientName || "-" },
    { key: "dob", title: "DOB", sortable: true, render: (p) => formatTableDate(p.dob) },
    { key: "model", title: "Modal #", sortable: true, render: (p) => <span dangerouslySetInnerHTML={{ __html: p.modelString || p.model || "-" }} /> },
    { key: "serial", title: "Serial #", sortable: true, render: (p) => <span dangerouslySetInnerHTML={{ __html: p.serialString || p.serial || "-" }} /> },
    { key: "rpmStartDate", title: "RPM Start Date", sortable: true, render: (p) => formatTableDate(p.rpmStartDate) },
    { key: "uploadDate", title: "Upload Date/Time", sortable: true, render: (p) => p.uploadDate || "-" },
    { key: "measurementDays", title: "Measurement Days", sortable: true, render: (p) => (p.measurementDays !== undefined ? p.measurementDays : "-") },
    { key: "serviceTime", title: "Service Time", sortable: true, render: (p) => p.serviceTime || "-" },
    { key: "eligibleCptCodes", title: "Eligible CPT Codes", sortable: true, render: (p) => <span dangerouslySetInnerHTML={{ __html: p.eligibleCptCodes || "-" }} /> }
  ];

  return (
    <div className="list-screen-page-container lookup-patient-rpm-page">
      <div className="list-screen-top-bar">
        <div className="list-screen-title-section">
          <Heading title="Look Up Patient (By RPM Date)" level="h2" className="list-screen-title-text" />
          {dateRangeDisplay ? (
            <div className="lookup-patient-rpm-subtitle">
              RPM Date Range: <span>{dateRangeDisplay}</span>
            </div>
          ) : null}
        </div>
        <div className="list-screen-actions">
          <ListScreenSearchBar
            value={search}
            onChange={(val) => {
              setSearch(val);
              setCurrentPage(1);
            }}
            inputId="lookup-patient-rpm-search"
          />
        </div>
      </div>

      <div className="lookup-patient-rpm-form-card lookup-patient-rpm-filter-card">
        <form className="lookup-patient-rpm-form" onSubmit={handleQuery}>
          <div className="lookup-patient-rpm-form-row">
            {isAdminOrSuper && !isScopedRole && (
              <div className="lookup-patient-rpm-practice-group">
                <label htmlFor="lookup-rpm-practice">Practice</label>
                <select
                  id="lookup-rpm-practice"
                  className="lookup-patient-rpm-practice-select"
                  value={practiceId}
                  onChange={(e) => setPracticeId(e.target.value)}
                >
                  <option value="">-- Select --</option>
                  {practices.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.practice_name || p.name || "Practice"}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="lookup-patient-rpm-date-group">
              <label>RPM Service Date Range</label>
              <div className="date-range-inputs mbr-rpm-date-range">
                <div className="mbr-date-field">
                  <input
                    ref={rpmStartInputRef}
                    type="date"
                    name="start"
                    className={`form-input-custom mbr-date-input${dateFieldErrors.start ? " lookup-patient-rpm-input-invalid" : ""}`}
                    value={formatDateForInput(dateRange.start)}
                    onChange={(e) => {
                      const date = e.target.value;
                      setDateRange((r) => {
                        if (date && r.end && date > r.end) {
                          setDateFieldErrors({
                            start: "Start date cannot be after end date.",
                            end: "",
                          });
                          return r;
                        }
                        setDateFieldErrors({ start: "", end: "" });
                        return { ...r, start: date };
                      });
                    }}
                    onClick={(e) => {
                      try {
                        e.currentTarget.showPicker?.();
                      } catch {
                        /* ignore */
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="mbr-date-calendar-btn"
                    aria-label="Open start date calendar"
                    onClick={() => openDatePicker(rpmStartInputRef)}
                  >
                    <FontAwesomeIcon icon={faCalendarAlt} />
                  </button>
                </div>
                <span className="date-sep">~</span>
                <div className="mbr-date-field">
                  <input
                    ref={rpmEndInputRef}
                    type="date"
                    name="end"
                    className={`form-input-custom mbr-date-input${dateFieldErrors.end ? " lookup-patient-rpm-input-invalid" : ""}`}
                    value={formatDateForInput(dateRange.end)}
                    min={dateRange.start || undefined}
                    onChange={(e) => {
                      const date = e.target.value;
                      setDateRange((r) => {
                        if (date && r.start && date < r.start) {
                          setDateFieldErrors({
                            start: "",
                            end: "End date cannot be before start date.",
                          });
                          return r;
                        }
                        setDateFieldErrors({ start: "", end: "" });
                        return { ...r, end: date };
                      });
                    }}
                    onClick={(e) => {
                      try {
                        e.currentTarget.showPicker?.();
                      } catch {
                        /* ignore */
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="mbr-date-calendar-btn"
                    aria-label="Open end date calendar"
                    onClick={() => openDatePicker(rpmEndInputRef)}
                  >
                    <FontAwesomeIcon icon={faCalendarAlt} />
                  </button>
                </div>
              </div>
              {(dateFieldErrors.start || dateFieldErrors.end) && (
                <div className="lookup-patient-rpm-date-inline-errors">
                  {dateFieldErrors.start ? (
                    <p className="lookup-patient-rpm-field-error" role="alert">
                      {dateFieldErrors.start}
                    </p>
                  ) : null}
                  {dateFieldErrors.end ? (
                    <p className="lookup-patient-rpm-field-error" role="alert">
                      {dateFieldErrors.end}
                    </p>
                  ) : null}
                </div>
              )}
            </div>

            <div className="lookup-patient-rpm-actions">
              <button
                type="button"
                className="reset-btn"
                onClick={handleReset}
                disabled={!hasActiveFilters}
                title={!hasActiveFilters ? "No filters selected" : ""}
              >
                <FontAwesomeIcon icon={faUndo} /> Reset
              </button>
              <button
                type="submit"
                className="query-btn"
                disabled={querySubmitting}
              >
                <FontAwesomeIcon icon={faSearch} /> {querySubmitting ? "Querying..." : "Query"}
              </button>
              <button
                type="button"
                className="print-btn"
                onClick={handlePrint}
                disabled={!hasQueryAttempted || sortedAndFilteredPatients.length === 0}
              >
                <FontAwesomeIcon icon={faPrint} /> Print
              </button>

              <div className="export-dropdown lookup-patient-rpm-export-container">
                <button
                  type="button"
                  className="export-btn"
                  disabled={!hasQueryAttempted || sortedAndFilteredPatients.length === 0 || isExporting}
                  onClick={() => !isExporting && setShowExportMenu(!showExportMenu)}
                >
                  <FontAwesomeIcon icon={faFileExport} /> {isExporting ? "Exporting..." : "Export"} <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: '10px', marginLeft: '2px' }} />
                </button>
                <div className={`export-menu lookup-patient-rpm-export-menu ${showExportMenu ? 'show' : ''}`}>
                  <button type="button" disabled={isExporting} onClick={() => { handleExport('csv'); }}>Export to CSV</button>
                  <button type="button" disabled={isExporting} onClick={() => { handleExport('pdf'); }}>Export to PDF</button>
                </div>
              </div>

              <button
                type="button"
                className="email-btn"
                onClick={handleEmail}
                disabled={!hasQueryAttempted || sortedAndFilteredPatients.length === 0}
              >
                <FontAwesomeIcon icon={faEnvelope} /> Email
              </button>
            </div>
          </div>
        </form>
      </div>

      <div className="lookup-patient-rpm-table-section">
        <div className="lookup-patient-rpm-table-wrapper">
          <Table
            columns={columns}
            data={sortedAndFilteredPatients}
            sortBy={sortConfig.key}
            sortOrder={sortConfig.direction === "asc" ? "asc" : "desc"}
            onSort={requestSort}
            isLoading={tableLoading || querySubmitting}
            loadingMode="skeleton"
            skeletonRows={8}
            onRowClick={handlePatientRowClick}
            getRowKey={(patient, index) =>
              `${resolvePatientPracticeId(patient, practiceId) || "p"}-${resolvePatientRecordId(patient) || index}`
            }
            emptyMessage="No patients found"
          />
        </div>

        {hasQueryAttempted && (
          <ListScreenPaginationBar
            entriesValue={entriesPerPage === "all" ? "all" : String(entriesPerPage)}
            onEntriesChange={(value) => {
              const nextEntries = value === "all" ? "all" : String(value);
              setEntriesPerPage(nextEntries);
              setCurrentPage(1);
              if (hasQueryAttempted && activeRpmQuery.practiceId) {
                loadRpmPatients(activeRpmQuery, 1, resolvePageLimit(nextEntries), {
                  silent: false,
                  showToast: false,
                  updateSubtitle: false
                });
              }
            }}
            showPageSelector={entriesPerPage !== "all" && entriesPerPage !== "All"}
            currentPage={safeCurrentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </div>

      <EmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        defaultSubject={defaultEmailSubject}
        defaultContent=""
        onSend={async (emailData) => {
          try {
            if (emailData.attachments?.length) {
              toast.info("Attachments are not supported yet. Sending without attachments.");
            }
            const payload = {
              to: emailData.to,
              cc: emailData.cc,
              bcc: emailData.bcc,
              subject: emailData.subject,
              text: emailData.content,
              html: `<pre style="font-family: Arial, sans-serif; white-space: pre-wrap;">${String(
                emailData.content || ""
              ).replace(/</g, "&lt;")}</pre>`,
              event_id: 11
            };
            const res = await emailService.sendCustomNotification(payload);
            if (!res.success) throw new Error(res.error || res.message);
            toast.success("Email queued successfully");
            setShowEmailModal(false);
          } catch (err) {
            toast.error(err?.message || "Failed to send email");
          }
        }}
      />
    </div>
  );
};

export default LookupPatientRPM;
